import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { IdentityProvider } from "api/controlPlaneApi";
import {
  addIdentityProvider,
  deleteIdentityProvider,
  editIdentityProvider,
  fetchIdentityProviderDetails,
  fetchIdentityProviders,
} from "./identityProvidersThunks";

export interface IdentityProvidersState {
  loadingIdentityProviders: boolean;
  loadingIdentityProviderDetails: boolean;
  loadingEditIdentityProvider: boolean;
  loadingDeleteIdentityProvider: boolean;
  loadingAddIdentityProvider: boolean;
  identityProviders: IdentityProvider[];
  identityProviderDetails: IdentityProvider | null;
}

const initialState: IdentityProvidersState = {
  loadingIdentityProviders: false,
  loadingIdentityProviderDetails: false,
  loadingEditIdentityProvider: false,
  loadingDeleteIdentityProvider: false,
  loadingAddIdentityProvider: false,
  identityProviders: [],
  identityProviderDetails: null,
};

export const identityProviderSlice = createSlice({
  name: "identityProvider",
  initialState,
  reducers: {
    setLoadingIdentityProviders: (state, action: PayloadAction<boolean>) => {
      state.loadingIdentityProviders = action.payload;
    },
    setLoadingIdentityProviderDetails: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.loadingIdentityProviderDetails = action.payload;
    },
    setLoadingDeleteIdentityProvider: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.loadingDeleteIdentityProvider = action.payload;
    },
    setLoadingAddIdentityProvider: (state, action: PayloadAction<boolean>) => {
      state.loadingAddIdentityProvider = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIdentityProviders.fulfilled, (state, action) => {
        state.loadingIdentityProviders = false;
        state.identityProviders = action.payload;
      })
      .addCase(fetchIdentityProviders.pending, (state) => {
        state.loadingIdentityProviders = true;
      })
      .addCase(fetchIdentityProviders.rejected, (state, action) => {
        state.loadingIdentityProviders = false;
        state.identityProviders = [];
      })
      .addCase(fetchIdentityProviderDetails.fulfilled, (state, action) => {
        state.loadingIdentityProviderDetails = false;
        state.identityProviderDetails = action.payload;
      })
      .addCase(fetchIdentityProviderDetails.pending, (state) => {
        state.loadingIdentityProviders = true;
      })
      .addCase(fetchIdentityProviderDetails.rejected, (state, action) => {
        state.loadingIdentityProviderDetails = false;
        state.identityProviderDetails = null;
      })
      .addCase(deleteIdentityProvider.fulfilled, (state, action) => {
        state.loadingDeleteIdentityProvider = false;
      })
      .addCase(deleteIdentityProvider.pending, (state) => {
        state.loadingDeleteIdentityProvider = true;
      })
      .addCase(deleteIdentityProvider.rejected, (state, action) => {
        state.loadingDeleteIdentityProvider = false;
      })
      .addCase(addIdentityProvider.fulfilled, (state, action) => {
        state.loadingAddIdentityProvider = false;
      })
      .addCase(addIdentityProvider.pending, (state) => {
        state.loadingAddIdentityProvider = true;
      })
      .addCase(addIdentityProvider.rejected, (state, action) => {
        state.loadingAddIdentityProvider = false;
      })
      .addCase(editIdentityProvider.fulfilled, (state, action) => {
        state.loadingEditIdentityProvider = false;
      })
      .addCase(editIdentityProvider.pending, (state) => {
        state.loadingEditIdentityProvider = true;
      })
      .addCase(editIdentityProvider.rejected, (state, action) => {
        state.loadingEditIdentityProvider = false;
      });
  },
});

// Reducer actions
export const {
  setLoadingIdentityProviders,
  setLoadingIdentityProviderDetails,
  setLoadingDeleteIdentityProvider,
  setLoadingAddIdentityProvider,
} = identityProviderSlice.actions;

// Reducer selectors

export const identityProviders = (state: RootState) =>
  state.identityProviders.identityProviders;
export const identityProviderDetails = (state: RootState) =>
  state.identityProviders.identityProviderDetails;
export const loadingIdentityProviders = (state: RootState) =>
  state.identityProviders.loadingIdentityProviders;
export const loadingIdentityProviderDetails = (state: RootState) =>
  state.identityProviders.loadingIdentityProviderDetails;
export const loadingDeleteIdentityProvider = (state: RootState) =>
  state.identityProviders.loadingDeleteIdentityProvider;
export const loadingAddIdentityProvider = (state: RootState) =>
  state.identityProviders.loadingAddIdentityProvider;

export default identityProviderSlice.reducer;
